import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { MockBadge, Button, Spinner } from "../components/ui";
import { Sun, Send, Settings, Sparkles, ArrowLeft, Mail, MessageCircle, Hammer } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";
import { staleNote } from "../lib/useRateDefault";

export default function Briefs() {
  const { t } = useI18n();
  const [list, setList] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showPrefs, setShowPrefs] = useState(false);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    const [l, p] = await Promise.all([api.get("/digest/list"), api.get("/digest/preferences")]);
    setList(l.data.briefs);
    setPrefs(p.data);
  };

  const load = async () => {
    setErr(null);
    try {
      await refresh();
    } catch (e) {
      console.error("Briefs load failed", e);
      setErr(t("common.loadFailed"));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendNow = async () => {
    setBusy(true);
    try { await api.post("/digest/send-now"); await refresh(); }
    finally { setBusy(false); }
  };

  const showPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/digest/preview?ai=true");
      setPreview(data);
    } finally { setBusy(false); }
  };

  const openBrief = async (id) => {
    const { data } = await api.get(`/digest/${id}`);
    setSelected(data);
  };

  if (err) return (
    <div className="p-10 text-center">
      <p className="text-sm text-signal-red font-mono">{err}</p>
      <button onClick={load} className="mt-3 text-xs font-mono uppercase tracking-widest2 text-signal-green hover:underline">
        {t("common.retry")}
      </button>
    </div>
  );
  if (!list || !prefs) return <div className="p-10"><Spinner /></div>;

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-5 border-b border-border bg-grid flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">{t("briefs.eyebrow")}</div>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight flex items-center gap-3">
            <Sun size={32} className="text-signal-yellow" /> {t("briefs.title")}
          </h1>
          <p className="mt-2 text-xs text-text-muted max-w-xl">
            {t("briefs.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={showPreview} variant="ghost" disabled={busy} data-testid="brief-preview-btn">
            {busy ? <Spinner /> : <Sparkles size={14} />} {t("briefs.previewToday")}
          </Button>
          <Button onClick={sendNow} variant="green" disabled={busy} data-testid="brief-send-now-btn">
            {busy ? <Spinner /> : <Send size={14} />} {t("briefs.sendNow")}
          </Button>
          <Button onClick={() => setShowPrefs(true)} variant="ghost" data-testid="brief-prefs-btn">
            <Settings size={14} /> {t("briefs.preferences")}
          </Button>
          <MockBadge />
        </div>
      </div>

      <div className="grid grid-cols-12">
        {/* LIST */}
        <section className="col-span-12 lg:col-span-4 border-r border-border p-6">
          <div className="overline">{t("briefs.archive")}</div>
          <h3 className="mt-1 font-heading text-2xl font-bold">{t("briefs.briefCount", { count: list.length, unit: list.length === 1 ? t("briefs.briefOne") : t("briefs.briefMany") })}</h3>
          {list.length === 0 ? (
            <div className="mt-5 border border-border p-5 text-center">
              <Sun size={28} className="mx-auto text-text-muted" />
              <p className="mt-3 text-xs font-mono text-text-secondary">
                {t("briefs.noBriefsYet", { time: prefs.delivery_time_utc })}
              </p>
            </div>
          ) : (
            <ol className="mt-4 space-y-2">
              {list.map((b) => (
                <li key={b.id}>
                  <button
                    onClick={() => openBrief(b.id)}
                    data-testid={`brief-row-${b.id}`}
                    className={`w-full text-left border p-3 transition-colors ${
                      selected?.id === b.id ? "border-signal-green bg-signal-green/5" : "border-border bg-surface hover:bg-surfaceHover"
                    }`}
                  >
                    <div className="overline">{new Date(b.generated_at).toLocaleString("en-GB")}</div>
                    <div className="mt-1 text-xs font-mono text-text-secondary flex items-center gap-3">
                      <span className="text-signal-green num">{b.alerts_count}</span> {t("briefs.alertsWord")}
                      {b.watch_drops_count > 0 && (
                        <span className="text-signal-yellow num">{b.watch_drops_count} {t("briefs.dropsWord")}</span>
                      )}
                      {b.market_pulse?.cycle && <span className="text-text-muted">{b.market_pulse.cycle}</span>}
                    </div>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </section>

        {/* DETAIL */}
        <section className="col-span-12 lg:col-span-8 p-6">
          {preview ? (
            <BriefView
              data={preview.brief}
              markdown={preview.markdown}
              label={t("briefs.labelPreview")}
              onClose={() => setPreview(null)}
              t={t}
            />
          ) : selected ? (
            <BriefView
              data={selected}
              markdown={selected.markdown}
              label={t("briefs.labelArchived")}
              onClose={() => setSelected(null)}
              t={t}
            />
          ) : (
            <div className="border border-border p-8 text-center">
              <Sun size={36} className="mx-auto text-text-muted" />
              <div className="mt-3 overline">{t("briefs.noBriefSelected")}</div>
              <p className="mt-2 text-xs text-text-secondary max-w-md mx-auto">
                {t("briefs.noBriefSelectedBody")}
              </p>
            </div>
          )}
        </section>
      </div>

      {showPrefs && (
        <PreferencesModal
          prefs={prefs}
          onClose={() => setShowPrefs(false)}
          onSaved={(p) => { setPrefs(p); setShowPrefs(false); }}
        />
      )}
    </div>
  );
}

function BriefView({ data, markdown, label, onClose, t }) {
  // fehlende Werte → „—" (wie render_markdown im Backend), niemals irreführende 0,0 %
  const pctDe = (v, d = 1) => (v == null ? "—" : (v * 100).toLocaleString("de-AT", { minimumFractionDigits: d, maximumFractionDigits: d }));
  return (
    <div className="reveal" data-testid="brief-view">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">{label} · {new Date(data.generated_at).toLocaleString("en-GB")}</div>
        <button onClick={onClose} className="overline hover:text-white inline-flex items-center gap-1.5">
          <ArrowLeft size={12} /> {t("briefs.back")}
        </button>
      </div>
      <div className="border border-border bg-surface p-6 ai-glow">
        {data.ai_intro && (
          <div className="border-l-2 border-signal-green pl-4 mb-5">
            <div className="overline text-signal-green inline-flex items-center gap-1.5">
              <Sparkles size={11} /> {t("briefs.editorial")}
            </div>
            <p className="mt-2 text-sm text-text-primary leading-relaxed italic">
              {data.ai_intro}
            </p>
          </div>
        )}

        {/* Markt-Puls */}
        <div className="overline">{t("briefs.marktPuls")}</div>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
          <Cell label={t("briefs.cellGreenPool")} value={data.market_pulse.green_now} accent="green" />
          <Cell label={t("briefs.cellDeltaLast")} value={`${data.market_pulse.green_delta >= 0 ? "+" : ""}${data.market_pulse.green_delta}`} accent={data.market_pulse.green_delta > 0 ? "green" : data.market_pulse.green_delta < 0 ? "red" : "white"} />
          <Cell label={t("briefs.cellNew")} value={data.market_pulse.new_count} />
          <Cell label={t("briefs.cellDrops")} value={data.market_pulse.price_drop_count} accent="yellow" />
        </div>

        {/* Mietmarkt-Puls (Alerts aus der rental_liquidity-Serie, Texte kommen fertig vom Backend) */}
        {data.rental_pulse?.alerts?.length > 0 && (
          <div className="mt-6">
            <div className="overline">{t("briefs.mietmarktPuls")}</div>
            <ul className="mt-2 space-y-1.5">
              {data.rental_pulse.alerts.map((a) => (
                <li key={`${a.market}-${a.kind}-${a.date}`} className="border border-border bg-bg p-3 text-xs font-mono text-text-secondary">
                  <span className="text-signal-yellow mr-2">⚠</span>{(a.text || "").replaceAll("*", "")}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Zins-Puls */}
        {data.rates?.available && (
          <>
            <div className="overline mt-6">{t("briefs.zinsPuls")}{data.rates.as_of ? ` · ${t("briefs.zinsPulsStand")} ${new Date(data.rates.as_of).toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit" })}` : ""}</div>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
              {/* Green reads as "this is the number" — a band the benchmark has
                  run away from must not. Yellow + the reason underneath. */}
              <Cell label={t("briefs.cellFix10y")} value={data.rates.fix_10y_privat?.low != null && data.rates.fix_10y_privat?.high != null ? `${pctDe(data.rates.fix_10y_privat.low)}–${pctDe(data.rates.fix_10y_privat.high)} %` : "—"} accent={data.rates.stale ? "yellow" : "green"} />
              <Cell label={t("briefs.cellGmbhAufschlag")} value={data.rates.fix_10y_gmbh_aufschlag != null ? `+${pctDe(data.rates.fix_10y_gmbh_aufschlag)} %` : "—"} />
              <Cell
                label={`${t("briefs.cellSwap10y")}${data.rates.swap_10y_as_of ? ` · ${new Date(data.rates.swap_10y_as_of).toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit" })}` : ""}`}
                value={data.rates.swap_10y != null ? `${pctDe(data.rates.swap_10y, 2)} %` : "—"}
              />
              <Cell label={t("briefs.cellEzbNext")} value={data.rates.ezb_next ? new Date(data.rates.ezb_next).toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit" }) : "—"} accent="yellow" />
            </div>
            {data.rates.stale && (
              <div data-testid="zins-stale" className="mt-2 border border-signal-yellow/40 bg-signal-yellow/5 p-3 text-xs font-mono text-signal-yellow">
                ⚠ {staleNote(data.rates, t, "briefs")}
              </div>
            )}
          </>
        )}

        {/* Top alerts */}
        {data.top_alerts?.length > 0 && (
          <div className="mt-6">
            <div className="overline">{t("briefs.topAlerts", { count: data.top_alerts.length })}</div>
            <ol className="mt-2 space-y-1.5">
              {data.top_alerts.map((a) => {
                const l = a.listing || {};
                return (
                  <li key={a.id} className="border border-border bg-bg p-3 flex items-center gap-3 text-xs font-mono">
                    <span className="num text-xl text-signal-green w-12">{l.faktor}</span>
                    <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-widest2 border ${l.zone?.includes("GREEN") ? "chip-green" : l.zone === "YELLOW" ? "chip-yellow" : "chip-red"}`}>{l.zone}</span>
                    <span className="flex-1 truncate text-text-secondary">{l.market_name} · {l.size_m2} m²{a.search_name ? ` · ${a.search_name}` : ""}</span>
                    <span className="num text-white">€{(l.price/1000).toFixed(0)}k</span>
                    {l.is_new && <span className="text-signal-green">🆕</span>}
                    {l.price_drop_pct && <span className="text-signal-yellow">−{l.price_drop_pct}%</span>}
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {/* Watchlist drops */}
        {data.watchlist_drops?.length > 0 && (
          <div className="mt-6">
            <div className="overline">{t("briefs.watchlistDrops")}</div>
            <ol className="mt-2 space-y-1.5">
              {data.watchlist_drops.map((w) => (
                <li key={w.id} className="border border-border bg-bg p-3 flex items-center gap-3 text-xs font-mono">
                  <span className="text-signal-yellow num">−{w.price_drop_pct}%</span>
                  <span className="flex-1 truncate text-text-secondary">{w.title}</span>
                  <span className="num text-white">€{(w.price/1000).toFixed(0)}k</span>
                  {w.previous_price && <span className="num text-text-muted line-through">€{(w.previous_price/1000).toFixed(0)}k</span>}
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Flip candidates */}
        {data.flip_candidates?.length > 0 && (
          <div className="mt-6">
            <div className="overline flex items-center gap-2"><Hammer size={11} /> {t("briefs.flipCandidates")}</div>
            <ol className="mt-2 space-y-1.5">
              {data.flip_candidates.map((f) => (
                <li key={f.id}>
                  <Link to={`/fixflip?id=${f.id}`} className="border border-border bg-bg p-3 flex items-center gap-3 text-xs font-mono hover:border-signal-green transition-colors">
                    <span className="num text-signal-green">{f.flip_diff_pct}%</span>
                    <span className="flex-1 truncate text-text-secondary">{f.title} · {f.market_name} · {f.size_m2} m²</span>
                    <span className="num text-text-muted">max €{((f.flip_max_ankauf || 0)/1000).toFixed(0)}k</span>
                    <span className="num text-white">€{(f.price/1000).toFixed(0)}k</span>
                  </Link>
                </li>
              ))}
            </ol>
            <p className="mt-1 text-[10px] font-mono text-text-muted">{t("briefs.flipScanNote")}</p>
          </div>
        )}

        {/* Raw markdown (collapsible) */}
        <details className="mt-6 group">
          <summary className="overline cursor-pointer hover:text-white">{t("briefs.viewRawMarkdown")}</summary>
          <pre className="mt-3 p-4 bg-bg border border-border text-[11px] font-mono text-text-secondary whitespace-pre-wrap overflow-x-auto">{markdown}</pre>
        </details>
      </div>
    </div>
  );
}

function Cell({ label, value, accent = "white" }) {
  const cls = { white: "text-white", green: "text-signal-green", yellow: "text-signal-yellow", red: "text-signal-red" }[accent];
  return (
    <div className="bg-bg p-3">
      <div className="overline">{label}</div>
      <div className={`num text-2xl mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

function PreferencesModal({ prefs, onClose, onSaved }) {
  const { t } = useI18n();
  const [enabled, setEnabled] = useState(prefs.enabled);
  const [time, setTime] = useState(prefs.delivery_time_utc);
  const [channels, setChannels] = useState(prefs.channels || []);
  const [ai, setAi] = useState(prefs.include_ai_intro);
  const [busy, setBusy] = useState(false);

  const toggleChan = (c) => setChannels(channels.includes(c) ? channels.filter((x) => x !== c) : [...channels, c]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/digest/preferences", {
        enabled, delivery_time_utc: time, channels, include_ai_intro: ai,
      });
      onSaved(data);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={onClose}
        aria-label={t("common.close")}
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
      />
      <div className="fixed inset-0 z-50 grid place-items-center p-4 pointer-events-none">
        <form
          onSubmit={save}
          data-testid="digest-prefs-modal"
          className="border border-border bg-bg w-full max-w-md pointer-events-auto reveal"
        >
          <div className="p-5 border-b border-border">
            <div className="overline">{t("briefs.dailyBrief")}</div>
            <h3 className="font-heading text-2xl font-bold mt-1">{t("briefs.preferences")}</h3>
          </div>
          <div className="p-5 space-y-5">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm">{t("briefs.enableDailyBrief")}</span>
              <input
                type="checkbox"
                data-testid="prefs-enabled"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="w-4 h-4 accent-signal-green"
              />
            </label>

            <div>
              <div className="overline">{t("briefs.deliveryTime")}</div>
              <input
                data-testid="prefs-time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                placeholder="05:00"
                pattern="[0-9]{2}:[0-9]{2}"
                className="mt-1 w-full bg-bg border border-border px-3 py-2 text-sm font-mono text-white focus:border-white outline-none"
              />
              <div className="mt-1 text-[10px] font-mono text-text-muted">
                {t("briefs.deliveryTimeNote")}
              </div>
            </div>

            <div>
              <div className="overline">{t("briefs.channels")}</div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {[
                  { id: "in_app", label: t("briefs.channelInApp"), icon: Mail },
                  { id: "telegram", label: t("briefs.channelTelegram"), icon: MessageCircle },
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    type="button"
                    key={id}
                    onClick={() => toggleChan(id)}
                    data-testid={`prefs-channel-${id}`}
                    className={`flex items-center gap-2 px-3 py-2 font-mono text-xs uppercase tracking-widest2 border transition-colors ${
                      channels.includes(id)
                        ? "border-signal-green text-signal-green bg-signal-green/10"
                        : "border-border text-text-muted hover:border-zinc-500"
                    }`}
                  >
                    <Icon size={14} /> {label}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm">{t("briefs.includeAiIntro")}</span>
              <input
                type="checkbox"
                data-testid="prefs-ai"
                checked={ai}
                onChange={(e) => setAi(e.target.checked)}
                className="w-4 h-4 accent-signal-green"
              />
            </label>
          </div>
          <div className="p-5 border-t border-border flex gap-2 justify-end">
            <Button type="button" variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" variant="green" disabled={busy} data-testid="prefs-save">
              {busy ? <Spinner /> : t("briefs.save")}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
